import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import styled from 'styled-components';
import CarregandoTabela from '../../components/CarregandoTabela';
import { COR_LINHA_CANCELADA, COR_LINHA_GESTOR_AVISADO, COR_LINHA_INCOMPLETA, COR_LINHA_PROTOCOLO, COR_LINHA_TERCEIRO, COR_LINHA_VX_VALIDA, descreverPendencias, getProgramacaoHavanColumns, getVxEstado, registroIncompleto } from './tanStackColumns';
import { aplicarFiltros } from '../utils/filtrosColunas';
import { aplicarLayoutColunas, LAYOUT_PADRAO } from '../utils/layoutColunas';
import { formatarDataHoraBr } from '../utils/dataBr';

const SEM_FILTROS = [];
// "Mostrar N": abre com 50 (a primeira opção é o padrão da tabela).
export const OPCOES_ITENS_POR_PAGINA = [50, 70, 100, 120];

// Prioridade da cor: cancelada (cinza) > gestor avisado (verde forte) > protocolo (roxo) > terceiro (laranja)
// > VX válida (verde bem clarinho) > incompleta (vermelho meio fraco: sem motorista ou sem veículo). Sem nada, branca.
const vxValida = registro => Boolean(registro) && getVxEstado(registro) === 'valida';
export const corDaLinha = registro => {
  if (registro?.cancelado) return COR_LINHA_CANCELADA;
  if (registro?.gestor_avisado) return COR_LINHA_GESTOR_AVISADO;
  if (registro?.protocolo) return COR_LINHA_PROTOCOLO;
  if (registro?.terceiro) return COR_LINHA_TERCEIRO;
  if (vxValida(registro)) return COR_LINHA_VX_VALIDA;
  return registroIncompleto(registro) ? COR_LINHA_INCOMPLETA : null;
};
const descreverAvisoGestor = registro => {
  if (!registro?.gestor_avisado) return null;
  const quando = formatarDataHoraBr(registro.gestor_avisado_em);
  return ['gestor avisado pelo WhatsApp', registro.gestor_avisado_por ? `por ${registro.gestor_avisado_por}` : null, quando ? `em ${quando}` : null]
    .filter(Boolean).join(' ');
};
export const tituloDaLinha = registro => {
  if (registro?.cancelado) return 'Carga cancelada na programação definitiva';
  const marcas = [
    descreverAvisoGestor(registro),
    registro?.protocolo ? 'marcada como protocolo' : null,
    registro?.terceiro ? 'liberada para terceiro' : null,
    vxValida(registro) ? 'consulta VX válida' : null,
    ...descreverPendencias(registro)
  ].filter(Boolean);
  return marcas.length ? `Carga ${marcas.join(', ')}` : undefined;
};

// onAlterarLarguras: recebe { alteradas: { idDaColuna: px }, resetadas: [idDaColuna] } quando o usuário solta a borda
// do cabeçalho ou dá duplo clique nela (volta ao padrão). A tela salva no navegador.
// versaoLarguras: muda quando as larguras voltam ao padrão pelo modal, para a tabela descartar as que estavam aplicadas.
const ProgramacaoHavanTable = ({ id, titulo, chaveDados = '', data = [], carregando = false, erro = null, pagina = 1, totalPaginas = 0, totalRegistros = 0, onChangePage, onRetry, inlineEditConfig, vxConfig, filtros = SEM_FILTROS, layoutColunas = LAYOUT_PADRAO, onAlterarLarguras, versaoLarguras = 0 }) => {
  const columns = useMemo(() => aplicarLayoutColunas(getProgramacaoHavanColumns(inlineEditConfig, vxConfig), layoutColunas), [inlineEditConfig, layoutColunas, vxConfig]);
  const [somenteTerceiros, setSomenteTerceiros] = useState(false);
  const [somenteIncompletas, setSomenteIncompletas] = useState(false);
  const [somenteProtocolo, setSomenteProtocolo] = useState(false);
  const hasPagination = totalPaginas > 1;
  const liberadasTerceiro = data.filter(registro => registro?.terceiro).length;
  const protocoladas = data.filter(registro => registro?.protocolo).length;
  // Mesma regra da linha vermelha: sem motorista ou sem veículo (frota/cavalo); cancelada não conta.
  const incompletas = data.filter(registroIncompleto).length;
  // Recarga após uma edição: a tabela continua na tela (mesma página e "Mostrar N"); só a primeira carga mostra o aviso.
  const atualizando = carregando && data.length > 0;

  // Outra data/página do servidor, ou nenhuma carga liberada (ex.: desmarcou a última): volta a mostrar todas.
  useEffect(() => { setSomenteTerceiros(false); setSomenteIncompletas(false); setSomenteProtocolo(false); }, [chaveDados, pagina]);
  useEffect(() => { if (incompletas === 0) setSomenteIncompletas(false); }, [incompletas]);
  useEffect(() => { if (liberadasTerceiro === 0) setSomenteTerceiros(false); }, [liberadasTerceiro]);
  useEffect(() => { if (protocoladas === 0) setSomenteProtocolo(false); }, [protocoladas]);

  const filtroAtivo = somenteTerceiros && liberadasTerceiro > 0;
  const filtroIncompletas = somenteIncompletas && incompletas > 0;
  const filtroProtocolo = somenteProtocolo && protocoladas > 0;
  const registrosVisiveis = useMemo(() => data
    .filter(registro => !filtroAtivo || registro?.terceiro)
    .filter(registro => !filtroProtocolo || registro?.protocolo)
    .filter(registro => !filtroIncompletas || registroIncompleto(registro))
    .filter(registro => aplicarFiltros([registro], filtros).length > 0), [data, filtroAtivo, filtroIncompletas, filtroProtocolo, filtros]);
  const filtrosAtivos = filtros.length > 0;
  const chaveFiltros = filtros.map(filtro => filtro.id).join(',');
  // Nova data ou nova página do servidor: aí sim a paginação da tabela recomeça na página 1.
  const chaveTabela = `${chaveDados}|${pagina}|${filtroAtivo ? 'terceiros' : 'todas'}|${filtroProtocolo ? 'protocolo' : 'todas'}|${filtroIncompletas ? 'incompletas' : 'todas'}|${chaveFiltros}|larguras-${versaoLarguras}`;

  // A tabela abre com as larguras salvas das colunas visíveis; o que ela avisar depois é comparado com isso para
  // salvar só o que o usuário mudou (um turno não desfaz o ajuste do outro) e o que voltou ao padrão (duplo clique).
  const idsVisiveis = useRef(new Set());
  idsVisiveis.current = new Set(columns.map(coluna => coluna.id));
  const largurasIniciais = useMemo(
    () => Object.fromEntries(Object.entries(layoutColunas.larguras || {}).filter(([colunaId]) => idsVisiveis.current.has(colunaId))),
    // Só quando a tabela recomeça (mesma regra da key): durante o uso, a própria tabela guarda as larguras.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chaveTabela]
  );
  const largurasAtuais = useRef(largurasIniciais);
  useEffect(() => { largurasAtuais.current = largurasIniciais; }, [largurasIniciais]);
  const avisarLarguras = useCallback(proximas => {
    const anteriores = largurasAtuais.current || {};
    largurasAtuais.current = proximas;
    const alteradas = Object.fromEntries(Object.entries(proximas).filter(([colunaId, largura]) => anteriores[colunaId] !== largura));
    // Coluna que só saiu da tabela (ocultada) não é "voltar ao padrão": a largura dela continua salva.
    const resetadas = Object.keys(anteriores).filter(colunaId => !(colunaId in proximas) && idsVisiveis.current.has(colunaId));
    if (Object.keys(alteradas).length || resetadas.length) onAlterarLarguras?.({ alteradas, resetadas });
  }, [onAlterarLarguras]);

  return (
    <TableSection id={id}>
      <TurnoTitle>
        <span>{titulo}{filtrosAtivos && data.length ? <ContagemFiltro> · {registrosVisiveis.length} de {data.length} com os filtros</ContagemFiltro> : null}</span>
        <TituloLado>
          {atualizando ? <Atualizando role="status">Atualizando…</Atualizando> : null}
          {incompletas > 0 ? (
            <LegendaIncompletas
              type="button"
              aria-pressed={filtroIncompletas}
              onClick={() => setSomenteIncompletas(atual => !atual)}
              title={filtroIncompletas ? 'Clique para voltar a mostrar todas as cargas do turno' : 'Clique para mostrar só as cargas sem motorista ou sem veículo (frota ou cavalo)'}
            >
              <AmostraIncompleta aria-hidden="true" />
              {filtroIncompletas ? `Mostrando ${incompletas} sem motorista ou veículo · ver todas` : `${incompletas} sem motorista ou veículo`}
            </LegendaIncompletas>
          ) : null}
          {protocoladas > 0 ? (
            <LegendaProtocolo
              type="button"
              aria-pressed={filtroProtocolo}
              onClick={() => setSomenteProtocolo(atual => !atual)}
              title={filtroProtocolo ? 'Clique para voltar a mostrar todas as cargas do turno' : 'Clique para mostrar só as cargas marcadas como protocolo'}
            >
              <AmostraProtocolo aria-hidden="true" />
              {filtroProtocolo ? `Mostrando ${protocoladas} com protocolo · ver todas` : `${protocoladas} com protocolo`}
            </LegendaProtocolo>
          ) : null}
          {liberadasTerceiro > 0 ? (
            <LegendaTerceiro
              type="button"
              aria-pressed={filtroAtivo}
              $ativo={filtroAtivo}
              onClick={() => setSomenteTerceiros(atual => !atual)}
              title={filtroAtivo ? 'Clique para voltar a mostrar todas as cargas do turno' : 'Clique para mostrar só as cargas liberadas para terceiro'}
            >
              <Amostra aria-hidden="true" />
              {filtroAtivo
                ? `Mostrando ${liberadasTerceiro} liberada(s) para terceiro · ver todas`
                : `${liberadasTerceiro} liberada(s) para terceiro`}
            </LegendaTerceiro>
          ) : null}
        </TituloLado>
      </TurnoTitle>
      <TableWrap>
        {carregando && !data.length ? <CarregandoTabela texto="Carregando programação…" /> : null}
        {!carregando && erro ? (
          <TableState>
            <span>{erro}</span>
            <RetryButton type="button" onClick={onRetry}>Tentar novamente</RetryButton>
          </TableState>
        ) : null}
        {!carregando && !erro && !data.length ? <TableState>Nenhuma programação encontrada para este turno.</TableState> : null}
        {!erro && data.length > 0 && registrosVisiveis.length === 0 && filtrosAtivos ? <TableState>Nenhuma carga deste turno atende aos filtros.</TableState> : null}
        {!erro && data.length > 0 && (registrosVisiveis.length > 0 || !filtrosAtivos) ? (
          <TanStackTable
            key={chaveTabela}
            columns={columns}
            // Arrastar a borda do cabeçalho: a linha-guia acompanha o mouse e a largura vale (e é salva) ao soltar.
            enableColumnResizing
            columnResizeMode="onEnd"
            initialState={{ columnSizing: largurasIniciais }}
            onColumnSizingChange={avisarLarguras}
            data={registrosVisiveis}
            isSortable
            pageSizeOptions={OPCOES_ITENS_POR_PAGINA}
            autoResetPageIndex={false}
            rowColorGetter={corDaLinha}
            rowTitleGetter={tituloDaLinha}
            enableCopy
            copyButtonPosition="start"
            className="programacao-havan-table"
          />
        ) : null}
      </TableWrap>
      {hasPagination ? (
        <Pagination>
          <span>Página {pagina} de {totalPaginas} · {totalRegistros} registros</span>
          <PaginationActions>
            <PaginationButton type="button" disabled={pagina <= 1} onClick={() => onChangePage?.(pagina - 1)}>Anterior</PaginationButton>
            <PaginationButton type="button" disabled={pagina >= totalPaginas} onClick={() => onChangePage?.(pagina + 1)}>Próxima</PaginationButton>
          </PaginationActions>
        </Pagination>
      ) : null}
    </TableSection>
  );
};

export default ProgramacaoHavanTable;

const TableSection = styled.section`margin-bottom: 20px; scroll-margin-top: 90px;`;
const TurnoTitle = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 12px; height: 28px; padding: 0 12px; border-radius: 2px 2px 0 0; background: #163455; color: #ffffff; font-size: 12px; font-weight: 600; text-transform: uppercase;`;
const TableWrap = styled.div`
  .programacao-havan-table { margin-top: 0 !important; }
  /* A tabela rola dentro da própria área (até a altura da tela) para o cabeçalho sticky ficar visível. */
  .programacao-havan-table > div { border-radius: 0 0 4px 4px; max-height: max(320px, calc(100vh - 230px)); }
  .programacao-havan-table table { min-width: 1560px; }
  .programacao-havan-table thead { z-index: 30; }
  .programacao-havan-table th {
    background: #ffffff;
    color: #24344d;
    font-size: 12px;
    font-weight: 600;
    padding: 6px 8px;
    /* Com o cabeçalho fixo, a borda inferior some (border-collapse): a sombra marca a separação das linhas. */
    box-shadow: inset 0 -2px 0 #d7e0ea, 0 2px 4px rgba(15, 23, 42, 0.06);
  }
  /* Linhas compactas: muitas cargas na tela de uma vez. */
  .programacao-havan-table td { color: #334155; font-size: 12px; line-height: 1.2; padding: 2px 8px; }
  /* A célula editável (compartilhada com o Depósito) traz altura mínima no estilo; aqui ela acompanha a linha compacta. */
  .programacao-havan-table td div[style*="min-height"] { min-height: 20px !important; }
`;
const TableState = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 88px; padding: 20px 12px; border: 1px solid #d7e0ea; border-top: 0; color: #526275; font-size: 13px;`;
const RetryButton = styled.button`padding: 6px 10px; border: 1px solid #163455; border-radius: 4px; background: #ffffff; color: #163455; font-size: 12px; font-weight: 600;`;
const Pagination = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 8px 12px; border: 1px solid #d7e0ea; border-top: 0; color: #526275; font-size: 12px;`;
const PaginationActions = styled.div`display: inline-flex; gap: 6px;`;
const PaginationButton = styled.button`padding: 5px 9px; border: 1px solid #163455; border-radius: 4px; background: #ffffff; color: #163455; font-size: 12px; &:disabled { opacity: 0.45; cursor: default; }`;
const LegendaTerceiro = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 2px 10px; border: 1px solid ${({ $ativo }) => ($ativo ? COR_LINHA_TERCEIRO : 'rgba(255, 255, 255, 0.35)')}; border-radius: 999px;
  background: ${({ $ativo }) => ($ativo ? COR_LINHA_TERCEIRO : 'transparent')};
  color: ${({ $ativo }) => ($ativo ? '#7c2d12' : '#ffffff')};
  font-size: 12px; font-weight: 600; text-transform: none; cursor: pointer;
  &:hover { border-color: ${COR_LINHA_TERCEIRO}; }
  &:focus-visible { outline: 2px solid ${COR_LINHA_TERCEIRO}; outline-offset: 2px; }
`;
const Amostra = styled.span`display: inline-block; width: 12px; height: 12px; border: 1px solid #f97316; border-radius: 2px; background: ${COR_LINHA_TERCEIRO};`;
const TituloLado = styled.span`display: inline-flex; align-items: center; gap: 16px;`;
const Atualizando = styled.span`text-transform: none; font-weight: 500; opacity: 0.85;`;
const LegendaIncompletas = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 2px 10px; border: 1px solid ${({ 'aria-pressed': pressionado }) => (pressionado ? COR_LINHA_INCOMPLETA : 'rgba(255, 255, 255, 0.35)')}; border-radius: 999px;
  background: ${({ 'aria-pressed': pressionado }) => (pressionado ? COR_LINHA_INCOMPLETA : 'transparent')};
  color: ${({ 'aria-pressed': pressionado }) => (pressionado ? '#7f1d1d' : '#ffffff')};
  font-size: 12px; font-weight: 600; text-transform: none; cursor: pointer;
  &:hover { border-color: ${COR_LINHA_INCOMPLETA}; }
  &:focus-visible { outline: 2px solid ${COR_LINHA_INCOMPLETA}; outline-offset: 2px; }
`;
const AmostraIncompleta = styled.span`display: inline-block; width: 12px; height: 12px; border: 1px solid #ef4444; border-radius: 2px; background: ${COR_LINHA_INCOMPLETA};`;
const LegendaProtocolo = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 2px 10px; border: 1px solid ${({ 'aria-pressed': pressionado }) => (pressionado ? COR_LINHA_PROTOCOLO : 'rgba(255, 255, 255, 0.35)')}; border-radius: 999px;
  background: ${({ 'aria-pressed': pressionado }) => (pressionado ? COR_LINHA_PROTOCOLO : 'transparent')};
  color: ${({ 'aria-pressed': pressionado }) => (pressionado ? '#581c87' : '#ffffff')};
  font-size: 12px; font-weight: 600; text-transform: none; cursor: pointer;
  &:hover { border-color: ${COR_LINHA_PROTOCOLO}; }
  &:focus-visible { outline: 2px solid ${COR_LINHA_PROTOCOLO}; outline-offset: 2px; }
`;
const AmostraProtocolo = styled.span`display: inline-block; width: 12px; height: 12px; border: 1px solid #7e22ce; border-radius: 2px; background: ${COR_LINHA_PROTOCOLO};`;
const ContagemFiltro = styled.span`text-transform: none; font-weight: 500; opacity: 0.9;`;
